import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  InputAdornment,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  VpnKey as PasswordIcon,
  CheckCircle as ActiveIcon,
  Person as PersonIcon,
  Public as PortalIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { benutzerApi, adressenApi, rollenApi } from '../services/api';
import type { Benutzer, CreateBenutzerDto, UpdateBenutzerDto, Adresse, Rolle, CreateRolleDto, BerechtigungenGruppiert } from '../types/api';
import { useAuth } from '../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const BenutzerVerwaltung: React.FC = () => {
  const { user } = useAuth();
  const [benutzer, setBenutzer] = useState<Benutzer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [adressen, setAdressen] = useState<Adresse[]>([]);

  // Dialog State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedBenutzer, setSelectedBenutzer] = useState<Benutzer | null>(null);
  const [saving, setSaving] = useState(false);

  // Password Reset Dialog
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);

  // Rollen Dialog (Benutzer-Zuweisung)
  const [rollenDialogOpen, setRollenDialogOpen] = useState(false);
  const [alleRollen, setAlleRollen] = useState<Rolle[]>([]);
  const [benutzerRollen, setBenutzerRollen] = useState<number[]>([]);
  const [savingRollen, setSavingRollen] = useState(false);

  // Rollen Verwaltung Dialog (Erstellen/Bearbeiten)
  const [rolleDialogOpen, setRolleDialogOpen] = useState(false);
  const [rolleDialogMode, setRolleDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedRolle, setSelectedRolle] = useState<Rolle | null>(null);
  const [rolleFormData, setRolleFormData] = useState<CreateRolleDto>({
    rolleName: '',
    beschreibung: '',
    berechtigungIDs: [],
  });
  const [savingRolle, setSavingRolle] = useState(false);
  const [alleBerechtigungen, setAlleBerechtigungen] = useState<BerechtigungenGruppiert[]>([]);

  // Form State
  const [formData, setFormData] = useState<CreateBenutzerDto>({
    mandantID: user?.mandantID || 1,
    benutzername: '',
    email: '',
    passwort: '',
    vorname: '',
    nachname: '',
    abteilung: '',
    status: 'aktiv',
    llmZugang: false,
    istPortalBenutzer: false,
    verknuepfteAdresseID: undefined,
  });

  useEffect(() => {
    loadBenutzer();
    loadAdressen();
    loadRollen();
    loadBerechtigungen();
  }, []);

  const loadRollen = async () => {
    try {
      const response = await rollenApi.getAll();
      setAlleRollen(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Rollen:', err);
    }
  };

  const loadBerechtigungen = async () => {
    try {
      const response = await rollenApi.getBerechtigungen();
      setAlleBerechtigungen(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Berechtigungen:', err);
    }
  };

  const loadBenutzer = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await benutzerApi.getAll();
      setBenutzer(response.data);
    } catch (err: any) {
      console.error('Fehler beim Laden der Benutzer:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  const loadAdressen = async () => {
    try {
      const response = await adressenApi.getAll({ take: 1000 });
      setAdressen(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Adressen:', err);
    }
  };

  const handleOpenCreateDialog = () => {
    setDialogMode('create');
    setSelectedBenutzer(null);
    setFormData({
      mandantID: user?.mandantID || 1,
      benutzername: '',
      email: '',
      passwort: '',
      vorname: '',
      nachname: '',
      abteilung: '',
      status: 'aktiv',
      llmZugang: false,
      istPortalBenutzer: false,
      verknuepfteAdresseID: undefined,
    });
    setDialogOpen(true);
  };

  const handleOpenEditDialog = (benutzerToEdit: Benutzer) => {
    setDialogMode('edit');
    setSelectedBenutzer(benutzerToEdit);
    setFormData({
      mandantID: benutzerToEdit.mandantID,
      benutzername: benutzerToEdit.benutzername,
      email: benutzerToEdit.email,
      passwort: '',
      vorname: benutzerToEdit.vorname || '',
      nachname: benutzerToEdit.nachname || '',
      abteilung: benutzerToEdit.abteilung || '',
      status: benutzerToEdit.status,
      llmZugang: benutzerToEdit.llmZugang,
      istPortalBenutzer: benutzerToEdit.istPortalBenutzer,
      verknuepfteAdresseID: benutzerToEdit.verknuepfteAdresseID,
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedBenutzer(null);
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      if (dialogMode === 'create') {
        if (!formData.passwort) {
          alert('Bitte Passwort eingeben');
          setSaving(false);
          return;
        }
        await benutzerApi.create(formData);
      } else if (selectedBenutzer) {
        const updateData: UpdateBenutzerDto = {
          vorname: formData.vorname,
          nachname: formData.nachname,
          email: formData.email,
          abteilung: formData.abteilung,
          status: formData.status,
          llmZugang: formData.llmZugang,
          istPortalBenutzer: formData.istPortalBenutzer,
          verknuepfteAdresseID: formData.verknuepfteAdresseID || 0,
        };
        await benutzerApi.update(selectedBenutzer.benutzerID, updateData);
      }

      handleCloseDialog();
      loadBenutzer();
    } catch (err: any) {
      console.error('Fehler beim Speichern:', err);
      alert(err.response?.data?.message || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Benutzer wirklich löschen?')) return;

    try {
      await benutzerApi.delete(id);
      loadBenutzer();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Fehler beim Löschen');
    }
  };

  const handleOpenPasswordDialog = (benutzerToReset: Benutzer) => {
    setSelectedBenutzer(benutzerToReset);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!selectedBenutzer || !newPassword) return;

    try {
      setResettingPassword(true);
      await benutzerApi.resetPassword(selectedBenutzer.benutzerID, { neuesPasswort: newPassword });
      setPasswordDialogOpen(false);
      setSelectedBenutzer(null);
      setNewPassword('');
      alert('Passwort erfolgreich zurückgesetzt');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Fehler beim Zurücksetzen des Passworts');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleActivate = async (id: number) => {
    try {
      await benutzerApi.activate(id);
      loadBenutzer();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Fehler beim Aktivieren');
    }
  };

  const handleOpenRollenDialog = async (benutzerToEdit: Benutzer) => {
    setSelectedBenutzer(benutzerToEdit);
    try {
      const response = await rollenApi.getBenutzerRollen(benutzerToEdit.benutzerID);
      setBenutzerRollen(response.data.map((r) => r.rolleID));
    } catch (err) {
      console.error('Fehler beim Laden der Benutzer-Rollen:', err);
      setBenutzerRollen([]);
    }
    setRollenDialogOpen(true);
  };

  const handleToggleRolle = (rolleId: number) => {
    setBenutzerRollen((prev) =>
      prev.includes(rolleId)
        ? prev.filter((id) => id !== rolleId)
        : [...prev, rolleId]
    );
  };

  const handleSaveRollen = async () => {
    if (!selectedBenutzer) return;

    try {
      setSavingRollen(true);
      await rollenApi.setBenutzerRollen(selectedBenutzer.benutzerID, {
        rolleIDs: benutzerRollen,
      });
      setRollenDialogOpen(false);
      setSelectedBenutzer(null);
      alert('Rollen erfolgreich aktualisiert');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Fehler beim Speichern der Rollen');
    } finally {
      setSavingRollen(false);
    }
  };

  // Rollen-Verwaltung Funktionen
  const handleOpenCreateRolleDialog = () => {
    setRolleDialogMode('create');
    setSelectedRolle(null);
    setRolleFormData({ rolleName: '', beschreibung: '', berechtigungIDs: [] });
    setRolleDialogOpen(true);
  };

  const handleOpenEditRolleDialog = async (rolle: Rolle) => {
    setRolleDialogMode('edit');
    setSelectedRolle(rolle);

    // Rolle mit Berechtigungen laden
    try {
      const response = await rollenApi.getById(rolle.rolleID);
      const rolleDetails = response.data;
      setRolleFormData({
        rolleName: rolleDetails.rolleName,
        beschreibung: rolleDetails.beschreibung || '',
        berechtigungIDs: rolleDetails.berechtigungen?.map(b => b.berechtigungID) || [],
      });
    } catch (err) {
      console.error('Fehler beim Laden der Rolle:', err);
      setRolleFormData({
        rolleName: rolle.rolleName,
        beschreibung: rolle.beschreibung || '',
        berechtigungIDs: [],
      });
    }
    setRolleDialogOpen(true);
  };

  const handleSaveRolle = async () => {
    try {
      setSavingRolle(true);
      if (rolleDialogMode === 'create') {
        await rollenApi.create(rolleFormData);
      } else if (selectedRolle) {
        await rollenApi.update(selectedRolle.rolleID, {
          rolleName: rolleFormData.rolleName,
          beschreibung: rolleFormData.beschreibung,
          berechtigungIDs: rolleFormData.berechtigungIDs,
        });
      }
      setRolleDialogOpen(false);
      loadRollen();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Fehler beim Speichern der Rolle');
    } finally {
      setSavingRolle(false);
    }
  };

  const handleToggleBerechtigung = (berechtigungId: number) => {
    setRolleFormData((prev) => ({
      ...prev,
      berechtigungIDs: prev.berechtigungIDs?.includes(berechtigungId)
        ? prev.berechtigungIDs.filter((id) => id !== berechtigungId)
        : [...(prev.berechtigungIDs || []), berechtigungId],
    }));
  };

  const handleDeleteRolle = async (rolle: Rolle) => {
    if (!window.confirm(`Rolle "${rolle.rolleName}" wirklich löschen?`)) return;

    try {
      await rollenApi.delete(rolle.rolleID);
      loadRollen();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Fehler beim Löschen der Rolle');
    }
  };

  const generatePassword = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('de-DE');
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'aktiv': return 'success';
      case 'inaktiv': return 'warning';
      case 'gesperrt': return 'error';
      default: return 'default';
    }
  };

  // Filter users by type
  const mitarbeiter = benutzer.filter(b => !b.istPortalBenutzer);
  const portalBenutzer = benutzer.filter(b => b.istPortalBenutzer);

  const renderBenutzerTable = (users: Benutzer[]) => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Benutzername</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>E-Mail</TableCell>
            <TableCell>Abteilung</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>LLM</TableCell>
            {tabValue === 1 && <TableCell>Verknüpfte Adresse</TableCell>}
            <TableCell>Letzter Login</TableCell>
            <TableCell align="right">Aktionen</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {users.length === 0 ? (
            <TableRow>
              <TableCell colSpan={tabValue === 1 ? 9 : 8} align="center">
                Keine Benutzer vorhanden
              </TableCell>
            </TableRow>
          ) : (
            users.map((b) => (
              <TableRow key={b.benutzerID} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    {b.istPortalBenutzer ? (
                      <PortalIcon fontSize="small" color="secondary" />
                    ) : (
                      <PersonIcon fontSize="small" color="primary" />
                    )}
                    {b.benutzername}
                  </Box>
                </TableCell>
                <TableCell>{b.vollstaendigerName || '-'}</TableCell>
                <TableCell>{b.email}</TableCell>
                <TableCell>{b.abteilung || '-'}</TableCell>
                <TableCell>
                  <Chip
                    label={b.status}
                    size="small"
                    color={getStatusColor(b.status)}
                  />
                </TableCell>
                <TableCell>
                  {b.llmZugang ? (
                    <Chip label="Ja" size="small" color="info" />
                  ) : (
                    <Chip label="Nein" size="small" variant="outlined" />
                  )}
                </TableCell>
                {tabValue === 1 && (
                  <TableCell>
                    {b.verknuepfteAdresseName || '-'}
                  </TableCell>
                )}
                <TableCell>{formatDate(b.letzterLogin)}</TableCell>
                <TableCell align="right">
                  <Tooltip title="Bearbeiten">
                    <IconButton size="small" onClick={() => handleOpenEditDialog(b)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Passwort zurücksetzen">
                    <IconButton size="small" onClick={() => handleOpenPasswordDialog(b)}>
                      <PasswordIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Berechtigungen">
                    <IconButton size="small" color="secondary" onClick={() => handleOpenRollenDialog(b)}>
                      <SecurityIcon />
                    </IconButton>
                  </Tooltip>
                  {b.status === 'inaktiv' && (
                    <Tooltip title="Aktivieren">
                      <IconButton size="small" color="success" onClick={() => handleActivate(b.benutzerID)}>
                        <ActiveIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Löschen">
                    <IconButton size="small" color="error" onClick={() => handleDelete(b.benutzerID)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4">Benutzerverwaltung</Typography>
          <Box display="flex" gap={1}>
            <Tooltip title="Aktualisieren">
              <IconButton onClick={loadBenutzer} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDialog}
            >
              Neuer Benutzer
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
          <Tab
            label={`Mitarbeiter (${mitarbeiter.length})`}
            icon={<PersonIcon />}
            iconPosition="start"
          />
          <Tab
            label={`Portal-Benutzer (${portalBenutzer.length})`}
            icon={<PortalIcon />}
            iconPosition="start"
          />
          <Tab
            label={`Rollen (${alleRollen.length})`}
            icon={<SecurityIcon />}
            iconPosition="start"
          />
        </Tabs>

        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TabPanel value={tabValue} index={0}>
              {renderBenutzerTable(mitarbeiter)}
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              {renderBenutzerTable(portalBenutzer)}
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
              <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCreateRolleDialog}
                >
                  Neue Rolle
                </Button>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Beschreibung</TableCell>
                      <TableCell align="center">Benutzer</TableCell>
                      <TableCell align="right">Aktionen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {alleRollen.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          Keine Rollen vorhanden
                        </TableCell>
                      </TableRow>
                    ) : (
                      alleRollen.map((rolle) => (
                        <TableRow key={rolle.rolleID} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <SecurityIcon fontSize="small" color="secondary" />
                              <Typography fontWeight="medium">{rolle.rolleName}</Typography>
                              {rolle.istStandard && (
                                <Chip label="Standard" size="small" color="primary" variant="outlined" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>{rolle.beschreibung || '-'}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={rolle.benutzerAnzahl}
                              size="small"
                              color={rolle.benutzerAnzahl > 0 ? 'info' : 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Bearbeiten">
                              <IconButton size="small" onClick={() => handleOpenEditRolleDialog(rolle)}>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Löschen">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteRolle(rolle)}
                                disabled={rolle.benutzerAnzahl > 0}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>
          </>
        )}
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Neuen Benutzer erstellen' : 'Benutzer bearbeiten'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Benutzername"
              value={formData.benutzername}
              onChange={(e) => setFormData({ ...formData, benutzername: e.target.value })}
              fullWidth
              required
              disabled={dialogMode === 'edit'}
            />

            <Box display="flex" gap={2}>
              <TextField
                label="Vorname"
                value={formData.vorname}
                onChange={(e) => setFormData({ ...formData, vorname: e.target.value })}
                fullWidth
              />
              <TextField
                label="Nachname"
                value={formData.nachname}
                onChange={(e) => setFormData({ ...formData, nachname: e.target.value })}
                fullWidth
              />
            </Box>

            <TextField
              label="E-Mail"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
              required
            />

            {dialogMode === 'create' && (
              <TextField
                label="Passwort"
                type={showPassword ? 'text' : 'password'}
                value={formData.passwort}
                onChange={(e) => setFormData({ ...formData, passwort: e.target.value })}
                fullWidth
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} size="small">
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                      <Button
                        size="small"
                        onClick={() => setFormData({ ...formData, passwort: generatePassword() })}
                      >
                        Generieren
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            <TextField
              label="Abteilung"
              value={formData.abteilung}
              onChange={(e) => setFormData({ ...formData, abteilung: e.target.value })}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <MenuItem value="aktiv">Aktiv</MenuItem>
                <MenuItem value="inaktiv">Inaktiv</MenuItem>
                <MenuItem value="gesperrt">Gesperrt</MenuItem>
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.llmZugang}
                  onChange={(e) => setFormData({ ...formData, llmZugang: e.target.checked })}
                />
              }
              label="KI/LLM-Zugang"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.istPortalBenutzer}
                  onChange={(e) => setFormData({ ...formData, istPortalBenutzer: e.target.checked })}
                />
              }
              label="Portal-Benutzer (externer Mandant)"
            />

            {formData.istPortalBenutzer && (
              <FormControl fullWidth>
                <InputLabel>Verknüpfte Adresse</InputLabel>
                <Select
                  value={formData.verknuepfteAdresseID || ''}
                  label="Verknüpfte Adresse"
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      verknuepfteAdresseID: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                >
                  <MenuItem value="">
                    <em>Keine Verknüpfung</em>
                  </MenuItem>
                  {adressen.map((adresse) => (
                    <MenuItem key={adresse.adresseID} value={adresse.adresseID}>
                      {adresse.firma
                        ? `${adresse.firma} (${adresse.vollstaendigerName})`
                        : adresse.vollstaendigerName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formData.benutzername || !formData.email}
          >
            {saving ? <CircularProgress size={24} /> : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Passwort zurücksetzen</DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Neues Passwort für {selectedBenutzer?.benutzername} setzen:
            </Typography>
            <TextField
              label="Neues Passwort"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} size="small">
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                    <Button size="small" onClick={() => setNewPassword(generatePassword())}>
                      Generieren
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleResetPassword}
            disabled={resettingPassword || !newPassword}
          >
            {resettingPassword ? <CircularProgress size={24} /> : 'Passwort setzen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rollen/Berechtigungen Dialog */}
      <Dialog
        open={rollenDialogOpen}
        onClose={() => setRollenDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SecurityIcon color="secondary" />
            Berechtigungen für {selectedBenutzer?.benutzername}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Wählen Sie die Rollen, die diesem Benutzer zugewiesen werden sollen.
              Die Berechtigungen werden durch die zugewiesenen Rollen bestimmt.
            </Typography>

            {alleRollen.length === 0 ? (
              <Alert severity="info">
                Keine Rollen vorhanden. Bitte erstellen Sie zuerst Rollen in der Datenbank.
              </Alert>
            ) : (
              <Box display="flex" flexDirection="column" gap={1}>
                {alleRollen.map((rolle) => (
                  <Paper
                    key={rolle.rolleID}
                    variant="outlined"
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      bgcolor: benutzerRollen.includes(rolle.rolleID)
                        ? 'action.selected'
                        : 'background.paper',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    }}
                    onClick={() => handleToggleRolle(rolle.rolleID)}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Checkbox
                            checked={benutzerRollen.includes(rolle.rolleID)}
                            onChange={() => handleToggleRolle(rolle.rolleID)}
                            onClick={(e) => e.stopPropagation()}
                          />
                          <Typography variant="subtitle1" fontWeight="bold">
                            {rolle.rolleName}
                          </Typography>
                          {rolle.istStandard && (
                            <Chip label="Standard" size="small" color="primary" variant="outlined" />
                          )}
                        </Box>
                        {rolle.beschreibung && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ ml: 5 }}
                          >
                            {rolle.beschreibung}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        label={`${rolle.benutzerAnzahl} Benutzer`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRollenDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleSaveRollen}
            disabled={savingRollen}
            startIcon={savingRollen ? <CircularProgress size={16} /> : <SecurityIcon />}
          >
            {savingRollen ? 'Speichere...' : 'Rollen speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rolle Erstellen/Bearbeiten Dialog */}
      <Dialog
        open={rolleDialogOpen}
        onClose={() => setRolleDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {rolleDialogMode === 'create' ? 'Neue Rolle erstellen' : 'Rolle bearbeiten'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Rollenname"
              value={rolleFormData.rolleName}
              onChange={(e) => setRolleFormData({ ...rolleFormData, rolleName: e.target.value })}
              fullWidth
              required
              placeholder="z.B. Administrator, Bearbeiter, Leser"
            />
            <TextField
              label="Beschreibung"
              value={rolleFormData.beschreibung}
              onChange={(e) => setRolleFormData({ ...rolleFormData, beschreibung: e.target.value })}
              fullWidth
              multiline
              rows={2}
              placeholder="Beschreiben Sie die Berechtigungen dieser Rolle..."
            />

            <Typography variant="subtitle1" fontWeight="bold" mt={2}>
              Berechtigungen
            </Typography>

            {alleBerechtigungen.length === 0 ? (
              <Alert severity="info">
                Keine Berechtigungen vorhanden.
              </Alert>
            ) : (
              <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                {alleBerechtigungen.map((gruppe) => (
                  <Paper key={gruppe.ressourceTyp} variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary" mb={1}>
                      {gruppe.ressourceTyp}
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {gruppe.berechtigungen.map((berechtigung) => (
                        <FormControlLabel
                          key={berechtigung.berechtigungID}
                          control={
                            <Checkbox
                              size="small"
                              checked={rolleFormData.berechtigungIDs?.includes(berechtigung.berechtigungID) || false}
                              onChange={() => handleToggleBerechtigung(berechtigung.berechtigungID)}
                            />
                          }
                          label={
                            <Tooltip title={berechtigung.beschreibung || ''} arrow>
                              <span style={{ fontSize: '0.875rem' }}>
                                {berechtigung.aktion}
                              </span>
                            </Tooltip>
                          }
                          sx={{
                            minWidth: 150,
                            m: 0,
                            '& .MuiFormControlLabel-label': {
                              fontSize: '0.875rem'
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Paper>
                ))}
              </Box>
            )}

            <Box display="flex" justifyContent="flex-end" gap={1}>
              <Typography variant="body2" color="text.secondary">
                {rolleFormData.berechtigungIDs?.length || 0} Berechtigungen ausgewählt
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRolleDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={handleSaveRolle}
            disabled={savingRolle || !rolleFormData.rolleName}
          >
            {savingRolle ? <CircularProgress size={24} /> : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BenutzerVerwaltung;
